import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { FlaskConical, Globe, Info, LogOut, Network, Palette, Thermometer, UserRound, Wrench } from 'lucide-react';
import { esciDisponibile, esciOra } from '@/app/auth';
import type { ImpiantoSintesi } from '@emacomfort/domain';
import { SimulatorClient } from '@emacomfort/device-client';
import { useDevice, useStato } from '@/app/DeviceProvider';
import { useMarchio } from '@/app/BrandProvider';
import { MARCHI, type MarchioId } from '@/app/marchi';
import { usePreferenze } from '@/app/preferenze';
import { LINGUE } from '@/i18n';
import { Bottone, Campo, Card, Interruttore, Pagina, Segmenti, Select, cx, stili } from '@/ui';
import s from '../features.module.css';

function Demo() {
  const { t, i18n } = useTranslation();
  const { marchio, setMarchio, puoCambiare } = useMarchio();
  const { client, parco, impiantoId, selezionaImpianto } = useDevice();
  const impianto = useStato((st) => st.impianto);
  const sim = client instanceof SimulatorClient ? client : null;
  const [scenario, setScenario] = useState(() => sim?.scenario.stato() ?? null);
  const [impianti, setImpianti] = useState<ImpiantoSintesi[]>([]);

  useEffect(() => {
    setScenario(sim?.scenario.stato() ?? null);
  }, [sim]);

  useEffect(() => {
    void parco.listaImpianti().then(setImpianti);
  }, [parco, impiantoId]);

  // Demo controls (brand switch, plant picker, scenario) appear ONLY on a demo
  // host: on a per-installer host the brand is fixed by DNS (ADR-059 §4).
  if (!sim || !scenario || !impianto || !puoCambiare) return null;
  const aggiorna = () => setScenario(sim.scenario.stato());
  const sondaDemo = impianto.canali.find((c) => c.assegnazione.tipo === 'funzione' && c.assegnazione.funzione === 'LUMINOSITA') ?? impianto.canali.find((c) => c.direzione === 'INPUT' && c.assegnazione.tipo === 'funzione' && c.assegnazione.funzione === 'TEMPERATURA_ESTERNA');
  const lingua = i18n.language === 'en' ? 'en' : 'it';

  return (
    <Card titolo={t('impostazioni.demo.titolo')} icona={<FlaskConical size={20} aria-hidden />}>
      <p className={s.letturaNota}>{t('impostazioni.demo.nota')}</p>
      <Campo etichetta={t('impostazioni.demo.marchio')} nota={t('impostazioni.demo.marchioNota')}>
        <Segmenti
          etichetta={t('impostazioni.demo.marchio')}
          valore={marchio.id}
          onChange={(id: MarchioId) => setMarchio(id)}
          opzioni={(Object.keys(MARCHI) as MarchioId[]).map((id) => ({ valore: id, etichetta: `${MARCHI[id].nome} · ${MARCHI[id].claim[lingua].split('·')[0]?.trim() ?? ''}` }))}
        />
      </Campo>
      <Campo etichetta={t('impostazioni.demo.impianto')}>
        <Select value={impiantoId} onChange={(e) => selezionaImpianto(e.target.value)}>
          {impianti.map((i) => (
            <option key={i.id} value={i.id}>
              {i.nome} · {i.citta}
            </option>
          ))}
        </Select>
      </Campo>
      <div className={stili.etichetta}>{t('impostazioni.demo.scenari')}</div>
      <div className={s.impostazione}>
        <span>{t('impostazioni.demo.centralinaIrraggiungibile')}</span>
        <Interruttore
          acceso={!scenario.raggiungibile}
          onChange={(v) => {
            sim.scenario.setRaggiungibile(!v);
            aggiorna();
          }}
          etichetta={t('impostazioni.demo.centralinaIrraggiungibile')}
          testoAcceso={t('comune.si')}
          testoSpento={t('comune.no')}
        />
      </div>
      {sondaDemo && (
        <div className={s.impostazione}>
          <span>{t('impostazioni.demo.sondaScollegata', { canale: sondaDemo.id })}</span>
          <Interruttore
            acceso={scenario.sondeScollegate.includes(sondaDemo.id)}
            onChange={(v) => {
              sim.scenario.setSondaScollegata(sondaDemo.id, v);
              aggiorna();
            }}
            etichetta={t('impostazioni.demo.sondaScollegata', { canale: sondaDemo.id })}
            testoAcceso={t('comune.si')}
            testoSpento={t('comune.no')}
          />
        </div>
      )}
      {impianto.sorgenti.length > 1 &&
        impianto.sorgenti.map((so) => (
          <div key={so.id} className={s.impostazione}>
            <span>{t('impostazioni.demo.sorgenteGiu', { sorgente: so.nome })}</span>
            <Interruttore
              acceso={scenario.sorgentiIrraggiungibili.includes(so.id)}
              onChange={(v) => {
                sim.scenario.setSorgenteRaggiungibile(so.id, !v);
                aggiorna();
              }}
              etichetta={t('impostazioni.demo.sorgenteGiu', { sorgente: so.nome })}
              testoAcceso={t('comune.si')}
              testoSpento={t('comune.no')}
            />
          </div>
        ))}
      <Campo etichetta={t('impostazioni.demo.velocita')}>
        <Segmenti
          etichetta={t('impostazioni.demo.velocita')}
          valore={String(scenario.velocita)}
          onChange={(v) => {
            sim.scenario.setVelocita(Number(v));
            aggiorna();
          }}
          opzioni={['1', '5', '20'].map((v) => ({ valore: v, etichetta: t('impostazioni.demo.velocitaValore', { x: v }) }))}
        />
      </Campo>
    </Card>
  );
}

export function ImpostazioniPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { marchio } = useMarchio();
  const impianto = useStato((st) => st.impianto);
  const { lingua, unita, tema, setLingua, setUnita, setTema } = usePreferenze();

  return (
    <Pagina titolo={t('impostazioni.titolo')}>
      <div className={s.grigliaDue}>
        <Card titolo={t('impostazioni.lingua')} icona={<Globe size={20} aria-hidden />}>
          <Segmenti etichetta={t('impostazioni.lingua')} valore={lingua} onChange={setLingua} opzioni={LINGUE.map((l) => ({ valore: l, etichetta: l === 'it' ? 'Italiano' : 'English' }))} />
        </Card>
        <Card titolo={t('impostazioni.unita')} icona={<Thermometer size={20} aria-hidden />}>
          <Segmenti
            etichetta={t('impostazioni.unita')}
            valore={unita}
            onChange={setUnita}
            opzioni={[
              { valore: 'C', etichetta: '°C' },
              { valore: 'F', etichetta: '°F' },
            ]}
          />
        </Card>
        <Card titolo={t('impostazioni.tema')} icona={<Palette size={20} aria-hidden />}>
          <Segmenti
            etichetta={t('impostazioni.tema')}
            valore={tema}
            onChange={setTema}
            opzioni={[
              { valore: 'scuro', etichetta: t('impostazioni.temaScuro') },
              { valore: 'chiaro', etichetta: t('impostazioni.temaChiaro') },
            ]}
          />
        </Card>
        <Card titolo={t('impostazioni.rete')} icona={<Network size={20} aria-hidden />}>
          <p>{impianto?.centralina.host ? t('impostazioni.reteLan', { host: impianto.centralina.host }) : t('impostazioni.reteCloud')}</p>
          <p className={s.letturaNota}>{t('impostazioni.reteNota')}</p>
        </Card>
        <Card titolo={t('impostazioni.account')} icona={<UserRound size={20} aria-hidden />}>
          <p>
            <span className={stili.etichetta}>{t('impostazioni.residente')}</span>
            <br />
            {impianto?.cliente}
          </p>
        </Card>
        <Card titolo={t('impostazioni.areaTecnica')} icona={<Wrench size={20} aria-hidden />}>
          <p className={s.letturaNota}>{t('impostazioni.areaTecnicaNota')}</p>
          <Bottone variante="primario" onClick={() => navigate('/tecnico')}>
            {t('impostazioni.accedi')}
          </Bottone>
        </Card>
        <Card titolo={t('impostazioni.informazioni')} icona={<Info size={20} aria-hidden />}>
          <dl className={cx(stili.griglia)} style={{ gridTemplateColumns: 'auto 1fr', rowGap: 6, columnGap: 16 }}>
            <dt className={stili.muto}>{t('impostazioni.installatore')}</dt>
            <dd>{marchio.nome}</dd>
            <dt className={stili.muto}>{t('impostazioni.impianto')}</dt>
            <dd>
              {impianto?.nome} · {impianto?.indirizzo.via}, {impianto?.indirizzo.citta}
            </dd>
            <dt className={stili.muto}>{t('impostazioni.centralina')}</dt>
            <dd>{impianto?.centralina.modello}</dd>
            <dt className={stili.muto}>{t('impostazioni.firmware')}</dt>
            <dd>{impianto?.centralina.firmware}</dd>
            <dt className={stili.muto}>{t('app.poweredBy')}</dt>
            <dd>
              {t('app.versione')} {__APP_VERSION__}
            </dd>
          </dl>
        </Card>
        {esciDisponibile() && (
          <Card titolo={t('impostazioni.account')} icona={<LogOut size={20} aria-hidden />}>
            <p className={s.letturaNota}>{t('impostazioni.accountNota')}</p>
            <Bottone variante="pericolo" onClick={() => void esciOra()}>
              {t('impostazioni.esci')}
            </Bottone>
          </Card>
        )}
        <Demo />
      </div>
    </Pagina>
  );
}
